import { cloneDeep } from 'lodash-es'
import create from 'zustand'
import { combine, persist } from 'zustand/middleware'
import { defaultFloorplanPanStep, defaultFloorplanZoomFactor } from 'src/hooks/sceneArrowKeys'
import {
  defaultLoadRenderConfig,
  InnerForcesEnvelopeRenderConfig,
  LineLoadRenderConfig,
  LoadRenderConfig,
  mergeLoadRenderConfig,
  PointLoadRenderConfig,
} from './loadRenderConfig'

export type {
  InnerForcesEnvelopeRenderConfig,
  LineLoadRenderConfig,
  LoadRenderConfig,
  LoadTypeRenderConfig,
  PointLoadRenderConfig,
} from './loadRenderConfig'
export { defaultLoadRenderConfig } from './loadRenderConfig'

/** Snapshot of a scene element an AI instruction refers to, taken when the instruction is added. */
export interface AiInstructionElement {
  guid: string
  label: string
  /** Singular German element-type label ("Pfette", "Dach"); null when the type could not be resolved. */
  elementTypeLabel: string | null
}

/** A note the user wrote down for the AI agent, to be copied to the clipboard later. */
export interface AiInstruction {
  id: string
  text: string
  elements: AiInstructionElement[]
  createdAt: string
}

interface AppStoreState {
  // key: project id, value: session id
  projectSessions: Record<string, string>
  showSnowV2: boolean
  /**
   * Show stats panel for debugging purposes
   */
  showStats: boolean

  /**
   * Show notifications to the user (e.g. when calculation finished)
   */
  showNotifications: boolean

  /**
   * Enable smart suggestion prompts and indicators.
   */
  smartSuggestionsEnabled: boolean

  disableFormValidation: boolean

  loadDisplayScale: number

  loadRenderConfig: LoadRenderConfig

  /**
   * Opacity value for the "transparent" option (default: 0.1)
   */
  transparentOpacityV2: number

  /**
   * Show the tensile anchors as an "Anker" tab inside Results2 instead of the
   * Zugverbindertabelle bottom drawer. On by default.
   */
  results2IntegratedAnchorView: boolean

  /**
   * Opacity value for translucent IFC models (default: 0.3)
   */
  ifcTransparentOpacity: number

  supportConfigToCheckRelPositionTolerance: number

  /**
   * Camera dolly (zoom) speed (scroll wheel).
   */
  dollySpeed: number

  /**
   * World-space distance in metres the floorplan trucks per arrow-key press.
   */
  floorplanPanStep: number

  /**
   * Factor applied to the orthographic zoom per plus or minus press.
   * Plus multiplies by this, minus divides by it.
   */
  floorplanZoomFactor: number

  /**
   * Results2 list footer: apply list filter to 3D scene ("Filter anwenden").
   */
  results2Filter3d: boolean

  /**
   * Results2 list footer: camera framing on selection ("Fokus").
   */
  results2FokusCamera: boolean

  /**
   * Results2 list footer: isolate selection in 3D ("Transparenz").
   */
  results2FokusTransparency: boolean

  /**
   * Results2 element list: show only warning/error rows ("Nur Warnungen").
   */
  results2OnlyWarnings: boolean

  /**
   * Size clip-plane handles from visible scene geometry (default). When false, uses a fixed 40×40 m plane.
   */
  clipPlaneSizeFromScene: boolean

  /**
   * Dev tool: enable the "Scene Element Debug" click inspector and its overlay
   * box listing every object under the cursor.
   */
  sceneElementDebug: boolean

  /**
   * Experimental: render shape-like model elements (walls, slabs, roof slabs,
   * vertical slabs, vertical roof slabs) with the batched `PointShapesMesh`
   * (one draw call per group) in `ModelMeshV2` instead of the per-element
   * extruded `ShapeMesh` in `ModelMesh`. Elements render as flat (zero-thickness)
   * polygons. When enabled, `ModelMesh` skips those bodies to avoid double drawing.
   */
  batchedModelRenderingV2: boolean

  /**
   * Hide the old "Pläne" page from the editor's left sidebar: plans are generated and managed in
   * the "Planansicht". On by default.
   */
  disableOldPlans: boolean

  /**
   * The organisation the user is currently working in.
   *
   * Sent as `X-Organization-Guid` on every request (see `state/utils/request`),
   * which is what makes the library, the templates and newly created projects
   * follow the choice. Persisted, because it is a working context rather than a
   * navigation state: coming back tomorrow should land you where you left off.
   *
   * `null` means "not chosen", and the backend then answers for the user's
   * default (oldest) organisation — so a user in exactly one, which is nearly
   * everyone, never has to think about this.
   */
  activeOrganisationGuid: string | null

  /**
   * Home project list: show only the current user's own projects ("Nur meine Projekte").
   */
  showOnlyMyProjects: boolean

  /**
   * Instructions the user noted down for the AI agent, keyed by project guid.
   * Persisted so they survive a reload until they are handed off and removed.
   */
  aiInstructionsByProject: Record<string, AiInstruction[]>
}

const initialState: AppStoreState = {
  projectSessions: {},
  showSnowV2: false,
  showStats: false,
  showNotifications: false,
  smartSuggestionsEnabled: true,

  disableFormValidation: false,

  loadDisplayScale: 2000,

  loadRenderConfig: cloneDeep(defaultLoadRenderConfig),

  transparentOpacityV2: 0.1,

  results2IntegratedAnchorView: true,

  ifcTransparentOpacity: 0.3,

  supportConfigToCheckRelPositionTolerance: 0.0001,

  dollySpeed: 0.7,
  floorplanPanStep: defaultFloorplanPanStep,
  floorplanZoomFactor: defaultFloorplanZoomFactor,

  results2Filter3d: false,
  results2FokusCamera: false,
  results2FokusTransparency: true,
  results2OnlyWarnings: false,

  clipPlaneSizeFromScene: true,

  sceneElementDebug: false,

  batchedModelRenderingV2: false,

  disableOldPlans: true,

  activeOrganisationGuid: null,

  showOnlyMyProjects: true,

  aiInstructionsByProject: {},
}

/**
 * App store will be written to localStorage of browser and therefore
 * persistent. Use this store for any app settings that should be kept after
 * refresh
 */
const useAppStore = create(
  persist(
    combine(cloneDeep(initialState), set => ({
      addSession: (projectId: string, sessionId: string) =>
        set(state => ({
          projectSessions: {
            ...state.projectSessions,
            [projectId]: sessionId,
          },
        })),
      setShowSnow: (showSnow: boolean) => set({ showSnowV2: showSnow }),
      setShowStats: (showStats: boolean) => set({ showStats }),
      setShowNotifications: (showNotifications: boolean) => set({ showNotifications }),
      setSmartSuggestionsEnabled: (smartSuggestionsEnabled: boolean) =>
        set({ smartSuggestionsEnabled }),

      setDisableFormValidation: (disableFormValidation: boolean) => set({ disableFormValidation }),
      setLoadDisplayScale: (loadDisplayScale: number) => set({ loadDisplayScale }),
      setLoadRenderConfig: (loadRenderConfig: LoadRenderConfig) => set({ loadRenderConfig }),
      setLineLoadRenderConfig: (partial: Partial<LineLoadRenderConfig>) =>
        set(state => ({
          loadRenderConfig: {
            ...state.loadRenderConfig,
            lineLoads: { ...state.loadRenderConfig.lineLoads, ...partial },
          },
        })),
      setPointLoadRenderConfig: (partial: Partial<PointLoadRenderConfig>) =>
        set(state => ({
          loadRenderConfig: {
            ...state.loadRenderConfig,
            pointLoads: { ...state.loadRenderConfig.pointLoads, ...partial },
          },
        })),
      setInnerForcesEnvelopeRenderConfig: (partial: Partial<InnerForcesEnvelopeRenderConfig>) =>
        set(state => ({
          loadRenderConfig: {
            ...state.loadRenderConfig,
            innerForcesEnvelope: { ...state.loadRenderConfig.innerForcesEnvelope, ...partial },
          },
        })),
      setSupportConfigToCheckRelPositionTolerance: (
        supportConfigToCheckRelPositionTolerance: number,
      ) => set({ supportConfigToCheckRelPositionTolerance }),
      setDollySpeed: (dollySpeed: number) => set({ dollySpeed }),
      setFloorplanPanStep: (floorplanPanStep: number) => set({ floorplanPanStep }),
      setFloorplanZoomFactor: (floorplanZoomFactor: number) => set({ floorplanZoomFactor }),
      setTransparentOpacity: (transparentOpacity: number) =>
        set({
          transparentOpacityV2: transparentOpacity,
        }),
      setResults2IntegratedAnchorView: (results2IntegratedAnchorView: boolean) =>
        set({ results2IntegratedAnchorView }),
      setIfcTransparentOpacity: (ifcTransparentOpacity: number) =>
        set({
          ifcTransparentOpacity,
        }),
      setResults2Filter3d: (results2Filter3d: boolean) => set({ results2Filter3d }),
      setResults2FokusCamera: (results2FokusCamera: boolean) => set({ results2FokusCamera }),
      setResults2FokusTransparency: (results2FokusTransparency: boolean) =>
        set({ results2FokusTransparency }),
      setResults2OnlyWarnings: (results2OnlyWarnings: boolean) => set({ results2OnlyWarnings }),
      setClipPlaneSizeFromScene: (clipPlaneSizeFromScene: boolean) =>
        set({ clipPlaneSizeFromScene }),
      setSceneElementDebug: (sceneElementDebug: boolean) => set({ sceneElementDebug }),
      setBatchedModelRenderingV2: (batchedModelRenderingV2: boolean) =>
        set({ batchedModelRenderingV2 }),
      setDisableOldPlans: (disableOldPlans: boolean) => set({ disableOldPlans }),
      setActiveOrganisationGuid: (activeOrganisationGuid: string | null) =>
        set({ activeOrganisationGuid }),
      setShowOnlyMyProjects: (showOnlyMyProjects: boolean) => set({ showOnlyMyProjects }),
      addAiInstruction: (projectGuid: string, instruction: AiInstruction) =>
        set(state => ({
          aiInstructionsByProject: {
            ...state.aiInstructionsByProject,
            [projectGuid]: [...(state.aiInstructionsByProject[projectGuid] ?? []), instruction],
          },
        })),
      removeAiInstruction: (projectGuid: string, instructionId: string) =>
        set(state => ({
          aiInstructionsByProject: {
            ...state.aiInstructionsByProject,
            [projectGuid]: (state.aiInstructionsByProject[projectGuid] ?? []).filter(
              instruction => instruction.id !== instructionId,
            ),
          },
        })),
      clearAiInstructions: (projectGuid: string) =>
        set(state => ({
          aiInstructionsByProject: {
            ...state.aiInstructionsByProject,
            [projectGuid]: [],
          },
        })),
    })),
    {
      name: 'app-storage',
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<AppStoreState> & {
          loadTextScale?: number
          showLoadUnitsInScene?: boolean
        }

        let loadRenderConfig = mergeLoadRenderConfig(
          currentState.loadRenderConfig,
          persisted.loadRenderConfig,
        )

        if (persisted.loadTextScale !== undefined || persisted.showLoadUnitsInScene !== undefined) {
          loadRenderConfig = {
            ...loadRenderConfig,
            lineLoads: {
              ...loadRenderConfig.lineLoads,
              ...(persisted.loadTextScale !== undefined
                ? { textScale: persisted.loadTextScale }
                : {}),
              ...(persisted.showLoadUnitsInScene !== undefined
                ? { showUnitsInScene: persisted.showLoadUnitsInScene }
                : {}),
            },
            pointLoads: {
              ...loadRenderConfig.pointLoads,
              ...(persisted.loadTextScale !== undefined
                ? { textScale: persisted.loadTextScale }
                : {}),
              ...(persisted.showLoadUnitsInScene !== undefined
                ? { showUnitsInScene: persisted.showLoadUnitsInScene }
                : {}),
            },
          }
        }

        const {
          loadTextScale: _loadTextScale,
          showLoadUnitsInScene: _showLoadUnitsInScene,
          ...rest
        } = persisted

        return {
          ...currentState,
          ...rest,
          loadRenderConfig,
        }
      },
    },
  ),
)

export default useAppStore

export { initialState as appStoreInitialState }
